import React from 'react';
import { isMobile } from 'react-device-detect';
import moment, { Moment } from 'moment';
import { Grid, SvgIcon } from '@material-ui/core';
import base64 from 'base-64';
import { Colors } from '../../../../constants';
import { IJob } from '../../JobModels';
import JobDays from './JobDaysRow';
import Avatar from '../../../../library/components/Avatar';
import { plusOneDay } from '../../../forms/time-operations';
import { PrimaryButton, SecondaryButton, Text } from 'library';
import childIcon from '../../../../assets/icons/children_icon.svg';
import LocationIcon from '@material-ui/icons/LocationOn';
import { JobTypeIds } from '../../../../models/JobType';
import usePublicSessionTracking, { PublicSessionEventType } from '../../../shared/PublicSessionTracking';

interface IProps {
    job: IJob;
    preApply?: () => void;
    uuid?: string | null;
    overrideClick?: () => void;
}

interface IIcons {
    children: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}
const icons: IIcons = {
    children: childIcon,
};

const families_url = window.location.origin;

export default function PublicJobCard({ job, preApply = () => null, uuid, overrideClick }: IProps) {
    const size = isMobile ? 12 : 3;
    const { trackSession } = usePublicSessionTracking('Public Job Card');

    const contents = [getChildText(), getCityText()];

    function getChildText() {
        const kidPlural = job.kid_count > 1 ? ' Children' : ' Child';
        const petPlural = job.pet_count > 1 ? ' Pets' : ' Pet';

        const plural = job.job_type === JobTypeIds.PetCare ? petPlural : kidPlural;
        const count = job.job_type === JobTypeIds.PetCare ? job.pet_count : job.kid_count;
        const display = `${count + plural}`;
        return (
            <Grid container item>
                <Grid item alignItems="center" justify="center">
                    <div
                        style={{
                            backgroundColor: Colors.lightTurq,
                            height: 26,
                            width: 26,
                            borderRadius: 13,
                            textAlign: 'center',
                        }}
                    >
                        <SvgIcon
                            component={icons['children']}
                            viewBox="-6 -5 26 26"
                            style={{
                                color: Colors.darkNavy,
                            }}
                        />
                    </div>
                </Grid>
                <Grid item style={{ paddingLeft: 12 }}>
                    <Text variant="body1">{display}</Text>
                </Grid>
            </Grid>
        );
    }

    function getCityText() {
        let trimmed = job.city.trim();
        let city = trimmed.replace(/\w+/g, function (w) {
            return w[0].toUpperCase() + w.slice(1).toLowerCase();
        });
        const display = `${city}, ${job.state}`;
        return (
            <Grid container item>
                <Grid item>
                    <div
                        style={{
                            backgroundColor: Colors.lightTurq,
                            height: 26,
                            width: 26,
                            borderRadius: 13,
                        }}
                    >
                        <SvgIcon component={LocationIcon} viewBox="-2 0 26 26" style={{ color: Colors.darkNavy }} />
                    </div>
                </Grid>
                <Grid item style={{ flex: 1, paddingLeft: 12 }}>
                    <Text variant="body1">{display}</Text>
                </Grid>
            </Grid>
        );
    }

    const getPayDisplay = () => {
        let displayText = '';
        if (!job.pay_is_fixed) {
            let pay = 0;
            try {
                const suffix = job.ongoing ? 'weekly' : 'total';
                let hours = 0;
                if (job.times_of_day) {
                    job.times_of_day.map((day: any) => {
                        hours += getDailyHours(day);
                    });
                } else if (!job.times_of_day) {
                    hours = getOneTimeHours();
                }
                if (job.pay) {
                    pay = job.pay;
                } else {
                    pay = job.rate_max;
                }
                displayText = `Up to $${hours * pay} ${suffix}`;
            } catch (e) {
                console.log(e);
            }
        }
        return (
            <Text variant="body1" bold>
                {displayText}
            </Text>
        );
    };

    const getDailyHours = (dayTimes: any) => {
        let hours = 0;
        Object.keys(dayTimes).map((dow) => {
            let startTime = moment(dayTimes[dow].start, 'HH:mm:ss');
            let endTime = moment(dayTimes[dow].end, 'HH:mm:ss');
            let duration = moment.duration(endTime.diff(startTime));
            let timeHours = duration.asHours();
            hours += Math.floor(timeHours);
        });
        return hours;
    };

    const getOneTimeHours = () => {
        let hours = 0;
        let startTime = moment(job.start_date_time);
        let endTime = moment(job.end_time, 'HH:mm:ss');

        endTime.set({ date: startTime.date(), month: startTime.month(), year: startTime.year() });

        const jobIsOvernight = endTime?.isBefore(startTime);

        if (jobIsOvernight) {
            hours = overnightTimeString(startTime, endTime);
        } else {
            let duration = moment.duration(endTime.diff(startTime));
            let timeHours = duration.asHours();
            hours += Math.floor(timeHours);
        }
        return hours;
    };

    function overnightTimeString(start: Moment, end: Moment) {
        let hours = 0;
        const nextDay = plusOneDay(end);
        let duration = moment.duration(nextDay.diff(start));
        let timeHours = duration.asHours();
        return (hours += Math.floor(timeHours));
    }

    function getIndex(job: IJob) {
        return job.id % 5;
    }

    async function showMoreDetails() {
        if (!!overrideClick) {
            overrideClick();
        } else {
            trackSession('NAVIGATE_FROM', 'To Public Job Details', PublicSessionEventType.User, { job: job.id });

            const encodedAccessor = base64.encode(job.id.toString());
            let url = `${families_url}/public/jobs/${encodedAccessor}`;
            if (!!uuid) {
                url += `?search=${uuid}`;
            }

            window.open(url, '_self');
        }
    }

    return (
        <Grid
            container
            spacing={4}
            item
            direction="row"
            xs={12}
            sm={6}
            md={4}
            lg={size}
            style={{
                marginTop: 30,
                overflow: 'visible',
                position: 'relative',
                borderRadius: 18,
                boxShadow: '0px 3px 3px rgba(0,1,1,0.15)',
                backgroundColor: Colors.white,
                maxHeight: 375,
            }}
        >
            {job.headline ? (
                <Grid item xs={11} style={{ paddingBottom: 0 }}>
                    <Text bold variant="h2">
                        {job.headline}
                    </Text>
                </Grid>
            ) : null}
            <Grid container item>
                <Grid item style={{ position: 'relative' }} xs={2}>
                    <Avatar
                        index={getIndex(job)}
                        style={{
                            maxWidth: 72,
                            maxHeight: 72,
                        }}
                    />
                </Grid>
                <Grid
                    container
                    item
                    direction="row"
                    alignItems="flex-start"
                    justify="flex-end"
                    xs={10}
                    style={{
                        paddingLeft: 5,
                    }}
                >
                    {contents.map((content: any) => (
                        <Grid container item xs={10} style={{ paddingBottom: 12 }}>
                            {content}
                        </Grid>
                    ))}
                </Grid>
                <Grid container item xs={12}>
                    <JobDays job={job} noMargin />
                </Grid>
                <Grid container item xs={12} style={{ paddingBottom: 12 }}>
                    {getPayDisplay()}
                </Grid>
                <Grid container direction="row">
                    <Grid item>
                        <SecondaryButton onClick={() => showMoreDetails()} buttonStyle={{ maxWidth: '100%' }}>
                            View More
                        </SecondaryButton>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
